import React, { useState, useEffect, useRef } from 'react';
import { itemAPI, categoryAPI, unitAPI, vendorAPI, getBillUrl, downloadBill, viewBill } from '../lib/api';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { DataTable } from '../components/DataTable';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Pencil, Trash2, Package, Loader2, Upload, Image, Calendar, X, FileText, ExternalLink, Search, Eye, Download } from 'lucide-react';
import { toast } from 'sonner';
import { getStatusColor, formatDate } from '../lib/utils';
import axios from 'axios';

// Use relative URLs for production compatibility - always use empty string for deployed environments
const BACKEND_URL = '';

export const ItemsPage = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [uploadingBill, setUploadingBill] = useState(false);
  const fileInputRef = useRef(null);
  const billInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    category_id: '',
    default_unit_id: '',
    brand: '',
    preferred_vendor_id: '',
    min_stock_level: 0,
    reorder_level: 0,
    quantity: 0,
    purchase_price: 0,
    selling_price: 0,
    description: '',
    notes: '',
    image_url: '',
    expiry_date: '',
    purchase_date: '',
    bill_url: '',
    bill_number: '',
    status: 'active'
  });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [itemsRes, categoriesRes, unitsRes, vendorsRes] = await Promise.all([
        itemAPI.getAll(),
        categoryAPI.getAll({ status: 'active' }),
        unitAPI.getAll({ status: 'active' }),
        vendorAPI.getAll({ status: 'active' })
      ]);
      setItems(itemsRes.data);
      setCategories(categoriesRes.data);
      setUnits(unitsRes.data);
      setVendors(vendorsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = (item = null) => {
    if (item) {
      setSelectedItem(item);
      setFormData({
        name: item.name || '',
        code: item.code || '',
        category_id: item.category_id || '',
        default_unit_id: item.default_unit_id || '',
        brand: item.brand || '',
        preferred_vendor_id: item.preferred_vendor_id || '',
        min_stock_level: item.min_stock_level || 0,
        reorder_level: item.reorder_level || 0,
        quantity: item.quantity || 0,
        purchase_price: item.purchase_price || 0,
        selling_price: item.selling_price || 0,
        description: item.description || '',
        notes: item.notes || '',
        image_url: item.image_url || '',
        expiry_date: item.expiry_date || '',
        purchase_date: item.purchase_date || '',
        bill_url: item.bill_url || '',
        bill_number: item.bill_number || '',
        status: item.status || 'active'
      });
    } else {
      setSelectedItem(null);
      setFormData({
        name: '',
        code: '',
        category_id: '',
        default_unit_id: '',
        brand: '',
        preferred_vendor_id: '',
        min_stock_level: 0,
        reorder_level: 0,
        quantity: 0,
        purchase_price: 0,
        selling_price: 0,
        description: '',
        notes: '',
        image_url: '',
        expiry_date: '',
        purchase_date: '',
        bill_url: '',
        bill_number: '',
        status: 'active'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedItem(null);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload an image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/upload/image`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        // Construct full URL for the image
        const imageUrl = `${BACKEND_URL}${response.data.url}`;
        setFormData(prev => ({ ...prev, image_url: imageUrl }));
        toast.success('Image uploaded successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, image_url: '' }));
  };

  const handleBillUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF, image, Word, or Excel file');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size should be less than 10MB');
      return;
    }

    try {
      setUploadingBill(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/upload/document`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        const billUrl = `${BACKEND_URL}${response.data.url}`;
        setFormData(prev => ({ ...prev, bill_url: billUrl }));
        toast.success('Bill uploaded successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload bill');
    } finally {
      setUploadingBill(false);
      if (billInputRef.current) {
        billInputRef.current.value = '';
      }
    }
  };

  const handleRemoveBill = () => {
    setFormData(prev => ({ ...prev, bill_url: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const data = {
        ...formData,
        min_stock_level: parseFloat(formData.min_stock_level) || 0,
        reorder_level: parseFloat(formData.reorder_level) || 0
      };
      if (selectedItem) {
        await itemAPI.update(selectedItem.id, data);
        toast.success('Item updated successfully');
      } else {
        await itemAPI.create(data);
        toast.success('Item created successfully');
      }
      handleCloseDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await itemAPI.delete(selectedItem.id);
      toast.success('Item deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const filteredItems = items.filter(i => {
    const matchesSearch = i.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      i.code?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || i.category_id === filterCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Items</h1>
          <p className="text-muted-foreground mt-1">Manage your inventory items</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="rounded-full" data-testid="add-item-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-items-input"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[180px]" data-testid="filter-category-select">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            module="items"
            data={items}
            loading={loading}
            title="Items Master Report"
            columns={[
              { key: 'image_url', label: 'Image', sortable: false, render: (val, item) => (
                val ? (
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted">
                    <img src={getBillUrl(val)} alt={item.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                    <Package className="h-5 w-5 text-muted-foreground/50" />
                  </div>
                )
              )},
              { key: 'name', label: 'Name', render: (val) => <span className="font-medium">{val}</span> },
              { key: 'item_code', label: 'Code', render: (val) => val ? <span className="px-2 py-1 bg-muted rounded text-sm font-mono">{val}</span> : '-' },
              { key: 'category_name', label: 'Category' },
              { key: 'unit_name', label: 'Unit' },
              { key: 'min_stock_level', label: 'Min Stock' },
              { key: 'purchase_date', label: 'Purchase Date', render: (val) => val ? formatDate(val) : '-' },
              { key: 'expiry_date', label: 'Expiry', render: (val) => val ? (
                <span className={new Date(val) < new Date() ? 'text-red-600 font-medium' : ''}>{formatDate(val)}</span>
              ) : '-' },
              { key: 'bill_url', label: 'Bill', sortable: false, render: (val, item) => val ? (
                <div className="flex items-center gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="h-7 px-2"
                    onClick={() => viewBill(val)}
                    title="View Bill"
                  >
                    <Eye className="h-3 w-3 mr-1" /><span className="text-xs">{item.bill_number || 'View'}</span>
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="h-7 px-1 text-green-600"
                    onClick={() => downloadBill(val, `bill_${item.code || item.name}`)}
                    title="Download Bill"
                  >
                    <Download className="h-3 w-3" />
                  </Button>
                </div>
              ) : '-' },
              { key: 'status', label: 'Status', render: (val) => (
                <Badge className={getStatusColor(val)}>{val}</Badge>
              )},
            ]}
            renderActions={(item) => (
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(item)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => { setSelectedItem(item); setDeleteDialogOpen(true); }}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            )}
          />
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedItem ? 'Edit Item' : 'Add Item'}</DialogTitle>
            <DialogDescription>
              {selectedItem ? 'Update item details' : 'Add a new inventory item'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Item Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="item-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Item Code</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    data-testid="item-code-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category_id">Category</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                  >
                    <SelectTrigger data-testid="item-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="default_unit_id">Default Unit</Label>
                  <Select
                    value={formData.default_unit_id}
                    onValueChange={(value) => setFormData({ ...formData, default_unit_id: value })}
                  >
                    <SelectTrigger data-testid="item-unit-select">
                      <SelectValue placeholder="Select unit" />
                    </SelectTrigger>
                    <SelectContent>
                      {units.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name} ({u.symbol})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="brand">Brand</Label>
                  <Input
                    id="brand"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    data-testid="item-brand-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="preferred_vendor_id">Preferred Vendor</Label>
                  <Select
                    value={formData.preferred_vendor_id}
                    onValueChange={(value) => setFormData({ ...formData, preferred_vendor_id: value })}
                  >
                    <SelectTrigger data-testid="item-vendor-select">
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map((v) => (
                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="min_stock_level">Min Stock Level</Label>
                  <Input
                    id="min_stock_level"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.min_stock_level}
                    onChange={(e) => setFormData({ ...formData, min_stock_level: e.target.value })}
                    data-testid="item-min-stock-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reorder_level">Reorder Level</Label>
                  <Input
                    id="reorder_level"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.reorder_level}
                    onChange={(e) => setFormData({ ...formData, reorder_level: e.target.value })}
                    data-testid="item-reorder-input"
                  />
                </div>
              </div>

              {/* Quantity and Pricing Section */}
              <div className="space-y-2 p-4 rounded-lg border bg-blue-50/50">
                <Label className="text-base font-semibold">Quantity & Pricing</Label>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="quantity">Opening Quantity</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      data-testid="item-quantity-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="purchase_price">Purchase Price (₹)</Label>
                    <Input
                      id="purchase_price"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.purchase_price}
                      onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                      data-testid="item-purchase-price-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="selling_price">Selling Price (₹)</Label>
                    <Input
                      id="selling_price"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.selling_price}
                      onChange={(e) => setFormData({ ...formData, selling_price: e.target.value })}
                      data-testid="item-selling-price-input"
                    />
                  </div>
                </div>
              </div>
              
              {/* Image Upload Section */}
              <div className="space-y-2">
                <Label>Item Image</Label>
                <div className="flex items-start gap-4">
                  {/* Image Preview */}
                  {formData.image_url ? (
                    <div className="relative w-24 h-24 rounded-lg border overflow-hidden bg-muted">
                      <img 
                        src={getBillUrl(formData.image_url)} 
                        alt="Item" 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = '';
                          e.target.onerror = null;
                        }}
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-1 right-1 h-6 w-6"
                        onClick={handleRemoveImage}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-lg border-2 border-dashed border-muted-foreground/25 flex items-center justify-center bg-muted/50">
                      <Image className="h-8 w-8 text-muted-foreground/50" />
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <div className="flex-1 space-y-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="item-image-upload"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="w-full"
                      data-testid="upload-image-btn"
                    >
                      {uploading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      {uploading ? 'Uploading...' : 'Upload Image'}
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      JPG, PNG, GIF or WebP. Max 5MB.
                    </p>
                    
                    {/* Or enter URL */}
                    <div className="pt-2">
                      <Input
                        placeholder="Or paste image URL"
                        value={formData.image_url}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        data-testid="item-image-url-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Expiry Date Section */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="purchase_date" className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Purchase Date
                  </Label>
                  <Input
                    id="purchase_date"
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                    data-testid="item-purchase-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expiry_date" className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Expiry Date
                  </Label>
                  <Input
                    id="expiry_date"
                    type="date"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                    data-testid="item-expiry-date-input"
                  />
                </div>
              </div>

              {/* Bill/Invoice Upload Section */}
              <div className="space-y-2 p-4 rounded-lg border bg-muted/30">
                <Label className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Purchase Bill / Invoice
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bill_number" className="text-xs text-muted-foreground">Bill Number</Label>
                    <Input
                      id="bill_number"
                      placeholder="Enter bill/invoice number"
                      value={formData.bill_number}
                      onChange={(e) => setFormData({ ...formData, bill_number: e.target.value })}
                      data-testid="item-bill-number-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Bill Document</Label>
                    {formData.bill_url ? (
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => viewBill(formData.bill_url)}
                          className="flex items-center gap-2 text-sm text-primary hover:underline flex-1 truncate justify-start"
                        >
                          <FileText className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">View Bill</span>
                          <Eye className="h-3 w-3 flex-shrink-0" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-green-600"
                          onClick={() => downloadBill(formData.bill_url, `bill_${formData.code || formData.name}`)}
                          title="Download Bill"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={handleRemoveBill}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <input
                          ref={billInputRef}
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                          onChange={handleBillUpload}
                          className="hidden"
                          id="bill-upload"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => billInputRef.current?.click()}
                          disabled={uploadingBill}
                          className="w-full"
                          data-testid="upload-bill-btn"
                        >
                          {uploadingBill ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4 mr-2" />
                          )}
                          {uploadingBill ? 'Uploading...' : 'Upload Bill'}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Supported: PDF, Word, Excel, or images. Max 10MB.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="item-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  data-testid="item-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  data-testid="item-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-item-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {selectedItem ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Item</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedItem?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-item-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ItemsPage;
